import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { Customer, Inventar, InventarGeraet, ModellLebenszyklus } from '../lib/types'
import { ErrorBox, Loading } from './Feedback'
import { InventarExport } from './DokuAnbindung'

type Filter = '' | InventarGeraet['lebenszyklus']

const ZUSTAND: Record<InventarGeraet['lebenszyklus'], { text: string; klasse: string }> = {
  abgelaufen: { text: 'abgelaufen', klasse: 'badge badge-crit' },
  bald: { text: 'endet bald', klasse: 'badge badge-warn' },
  ok: { text: 'in Ordnung', klasse: 'badge badge-ok' },
  unbekannt: { text: 'keine Daten', klasse: 'badge badge-off' },
}

const ENDE_ART: Record<string, string> = {
  garantie: 'Garantie',
  wartung: 'Wartungsvertrag',
  hersteller: 'Hersteller-Support',
}

/**
 * Inventar mit Lebenszyklus (Stufe 4.4): je Gerät Garantie, Wartungsvertrag
 * und Support-Ende des Herstellers, dazu die Firmware-Stände je Modell.
 */
export function InventarBericht({ customer }: { customer?: Customer }) {
  const [filter, setFilter] = useState<Filter>('')
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  const { canForCustomer } = useAuth()

  const inventar = useQuery({
    queryKey: ['inventar', customer?.id ?? null],
    queryFn: ({ signal }) => apiGet<Inventar>(`/inventar${customer ? `?kunde=${customer.id}` : ''}`, signal),
  })

  if (inventar.isPending) return <Loading what="Das Inventar" />
  if (inventar.isError) return <ErrorBox error={inventar.error} what="Das Inventar" />
  const d = inventar.data
  const geraete = filter ? d.geraete.filter((g) => g.lebenszyklus === filter) : d.geraete
  const spalten = customer ? 9 : 10

  return (
    <>
      <InventarExport customer={customer} />
      <section className="card">
        <div className="card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h2 className="card-title">Lebenszyklus der Geräte</h2>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {(
              [
                ['', `Alle ${d.geraete.length}`],
                ['abgelaufen', `Abgelaufen ${d.anzahl.abgelaufen ?? 0}`],
                ['bald', `Endet bald ${d.anzahl.bald ?? 0}`],
                ['ok', `In Ordnung ${d.anzahl.ok ?? 0}`],
                ['unbekannt', `Keine Daten ${d.anzahl.unbekannt ?? 0}`],
              ] as [Filter, string][]
            ).map(([key, label]) => (
              <button
                key={key || 'alle'}
                type="button"
                className="btn btn-sm"
                aria-pressed={filter === key}
                style={
                  filter === key ? { background: 'var(--acc-soft)', color: 'var(--acc)', fontWeight: 600 } : undefined
                }
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body muted" style={{ fontSize: 13, paddingTop: 0 }}>
          „Endet bald“ heißt: Garantie, Wartungsvertrag oder Hersteller-Support enden in den nächsten {d.warnung_tage}{' '}
          Tagen. Dann gibt es auch einen Alarm am Gerät.
        </div>
        {geraete.length === 0 ? (
          <div className="empty">
            <span>Keine Geräte in dieser Auswahl.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                {customer ? null : <th>Kunde</th>}
                <th>Modell</th>
                <th>Firmware</th>
                <th>Seriennummer</th>
                <th>Garantie bis</th>
                <th>Wartung bis</th>
                <th>Hersteller-Support bis</th>
                <th>Zustand</th>
                <th aria-label="Aktion" />
              </tr>
            </thead>
            <tbody>
              {geraete.map((g) => (
                <GeraeteZeile
                  key={g.id}
                  g={g}
                  mitKunde={!customer}
                  spalten={spalten}
                  darf={canForCustomer(g.kunde_id, 'device.manage')}
                  offen={bearbeitet === g.id}
                  onOeffnen={() => setBearbeitet(bearbeitet === g.id ? null : g.id)}
                  onFertig={() => setBearbeitet(null)}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Firmware-Stände je Modell</h2>
        </div>
        {d.firmware.length === 0 ? (
          <div className="empty">
            <span>Noch keine Firmware erkannt.</span>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Hersteller · Modell</th>
                <th>Firmware</th>
                <th>Geräte</th>
                {customer ? null : <th>Kunden</th>}
                <th>Stand</th>
                <th>Firmware-Hinweise</th>
              </tr>
            </thead>
            <tbody>
              {d.firmware.map((f) => (
                <tr key={`${f.hersteller}-${f.modell}-${f.firmware}`}>
                  <td>
                    {f.hersteller} · {f.modell || 'unbekanntes Modell'}
                  </td>
                  <td className="mono">{f.firmware}</td>
                  <td>{f.geraete}</td>
                  {customer ? null : <td>{f.kunden}</td>}
                  <td>
                    {f.neueste ? (
                      <span className="badge badge-ok">neueste im Bestand</span>
                    ) : (
                      <span className="badge badge-warn">älter</span>
                    )}
                  </td>
                  <td>{f.hinweise > 0 ? <span className="badge badge-crit">{f.hinweise}</span> : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ModellListe />
    </>
  )
}

/** Ein Datum ohne Uhrzeit, wie es die Zentrale schickt (JJJJ-MM-TT); ohne Umweg über Zeitzonen. */
export function tagText(wert?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wert ?? '')
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '–'
}

function Datum({ wert }: { wert?: string }) {
  return <>{tagText(wert)}</>
}

function GeraeteZeile({
  g,
  mitKunde,
  spalten,
  darf,
  offen,
  onOeffnen,
  onFertig,
}: {
  g: InventarGeraet
  mitKunde: boolean
  spalten: number
  darf: boolean
  offen: boolean
  onOeffnen: () => void
  onFertig: () => void
}) {
  return (
    <>
      <tr>
        <td>
          <Link to="/k/$customerSlug/geraete/$deviceId" params={{ customerSlug: g.kunde_slug, deviceId: g.id }}>
            {g.name}
          </Link>
          <span className="muted" style={{ fontSize: 12, display: 'block' }}>
            {g.standort}
          </span>
        </td>
        {mitKunde ? <td>{g.kunde}</td> : null}
        <td>
          {g.hersteller} {g.modell}
        </td>
        <td>
          <span className="mono">{g.firmware || '–'}</span>
          {g.neueste_firmware ? (
            <span className="muted" style={{ fontSize: 12, display: 'block' }}>
              im Bestand schon {g.neueste_firmware}
            </span>
          ) : null}
          {g.hinweise > 0 ? (
            <span className="badge badge-crit" style={{ marginTop: 2 }}>
              {g.hinweise} {g.hinweise === 1 ? 'Hinweis' : 'Hinweise'}
            </span>
          ) : null}
        </td>
        <td className="mono">{g.seriennummer || '–'}</td>
        <td>
          <Datum wert={g.garantie_bis} />
        </td>
        <td>
          <Datum wert={g.wartung_bis} />
        </td>
        <td>
          <Datum wert={g.supportende} />
        </td>
        <td>
          <span className={ZUSTAND[g.lebenszyklus].klasse}>{ZUSTAND[g.lebenszyklus].text}</span>
          {g.naechstes_ende && g.naechstes_ende_art ? (
            <span className="muted" style={{ fontSize: 12, display: 'block' }}>
              {ENDE_ART[g.naechstes_ende_art]} {tagText(g.naechstes_ende)}
            </span>
          ) : null}
        </td>
        <td>
          {darf ? (
            <button type="button" className="btn btn-sm" aria-expanded={offen} onClick={onOeffnen}>
              Daten bearbeiten
            </button>
          ) : null}
        </td>
      </tr>
      {offen ? (
        <tr>
          <td colSpan={spalten}>
            <LebenszyklusFormular g={g} onFertig={onFertig} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

function LebenszyklusFormular({ g, onFertig }: { g: InventarGeraet; onFertig: () => void }) {
  const queryClient = useQueryClient()
  const [gekauft, setGekauft] = useState(g.gekauft_am ?? '')
  const [garantie, setGarantie] = useState(g.garantie_bis ?? '')
  const [wartung, setWartung] = useState(g.wartung_bis ?? '')
  const [notiz, setNotiz] = useState(g.notiz)
  const speichern = useMutation({
    mutationFn: () =>
      apiSend('PUT', `/devices/${g.id}/lebenszyklus`, {
        gekauft_am: gekauft || null,
        garantie_bis: garantie || null,
        wartung_bis: wartung || null,
        notiz,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventar'] })
      onFertig()
    },
  })
  const feld = (id: string, label: string, wert: string, set: (v: string) => void) => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="input" type="date" value={wert} onChange={(e) => set(e.target.value)} />
    </div>
  )
  return (
    <form
      className="row"
      style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
      onSubmit={(e) => {
        e.preventDefault()
        speichern.mutate()
      }}
    >
      {feld(`gekauft-${g.id}`, 'Gekauft am', gekauft, setGekauft)}
      {feld(`garantie-${g.id}`, 'Garantie bis', garantie, setGarantie)}
      {feld(`wartung-${g.id}`, 'Wartungsvertrag bis', wartung, setWartung)}
      <div className="field grow">
        <label htmlFor={`notiz-${g.id}`}>Notiz (z. B. Vertragsnummer)</label>
        <input
          id={`notiz-${g.id}`}
          className="input"
          maxLength={500}
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
        />
      </div>
      <button type="submit" className="btn btn-primary btn-sm" disabled={speichern.isPending}>
        Speichern
      </button>
      <button type="button" className="btn btn-sm" onClick={onFertig}>
        Abbrechen
      </button>
      {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
    </form>
  )
}

/** Support-Ende je Modell: gilt für alle Kunden, gepflegt wie die Firmware-Hinweise. */
function ModellListe() {
  const { me } = useAuth()
  const queryClient = useQueryClient()
  const darf = me?.global_permissions.includes('settings.manage') ?? false
  const liste = useQuery({
    queryKey: ['modell-lebenszyklus'],
    queryFn: ({ signal }) => apiGet<ModellLebenszyklus[]>('/modell-lebenszyklus', signal),
  })
  const [hersteller, setHersteller] = useState('')
  const [modell, setModell] = useState('')
  const [verkaufsende, setVerkaufsende] = useState('')
  const [supportende, setSupportende] = useState('')
  const [url, setUrl] = useState('')
  const neu = () => {
    void queryClient.invalidateQueries({ queryKey: ['modell-lebenszyklus'] })
    void queryClient.invalidateQueries({ queryKey: ['inventar'] })
  }
  const speichern = useMutation({
    mutationFn: () =>
      apiSend('POST', '/modell-lebenszyklus', {
        hersteller,
        modell,
        verkaufsende: verkaufsende || null,
        supportende: supportende || null,
        url,
        notiz: '',
      }),
    onSuccess: () => {
      setHersteller('')
      setModell('')
      setVerkaufsende('')
      setSupportende('')
      setUrl('')
      neu()
    },
  })
  const loeschen = useMutation({
    mutationFn: (id: string) => apiSend('DELETE', `/modell-lebenszyklus/${id}`),
    onSuccess: neu,
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Support-Ende je Modell</h2>
      </div>
      <div className="card-body muted" style={{ fontSize: 13, paddingTop: 0 }}>
        Angaben des Herstellers (End of Sale, End of Support). Sie gelten für alle Geräte mit genau diesem Hersteller
        und Modell, bei allen Kunden. Quelle als Link angeben.
      </div>
      {liste.isPending ? (
        <Loading what="Die Modell-Liste" />
      ) : liste.isError ? (
        <ErrorBox error={liste.error} what="Die Modell-Liste" />
      ) : liste.data.length === 0 ? (
        <div className="empty">
          <span>Noch keine Modelle eingetragen.</span>
        </div>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Hersteller · Modell</th>
              <th>Verkaufsende</th>
              <th>Support-Ende</th>
              <th>Quelle</th>
              {darf ? <th aria-label="Aktion" /> : null}
            </tr>
          </thead>
          <tbody>
            {liste.data.map((m) => (
              <tr key={m.id}>
                <td>
                  {m.hersteller} · {m.modell}
                </td>
                <td>
                  <Datum wert={m.verkaufsende} />
                </td>
                <td>
                  <Datum wert={m.supportende} />
                </td>
                <td>
                  {m.url ? (
                    <a href={m.url} target="_blank" rel="noreferrer noopener">
                      Hersteller
                    </a>
                  ) : (
                    '–'
                  )}
                </td>
                {darf ? (
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={loeschen.isPending}
                      onClick={() => {
                        if (window.confirm(`${m.hersteller} ${m.modell} aus der Liste entfernen?`))
                          loeschen.mutate(m.id)
                      }}
                    >
                      Entfernen
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {darf ? (
        <form
          className="card-body row"
          style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--border)' }}
          onSubmit={(e) => {
            e.preventDefault()
            speichern.mutate()
          }}
        >
          <div className="field">
            <label htmlFor="modell-hersteller">Hersteller</label>
            <input
              id="modell-hersteller"
              className="input"
              value={hersteller}
              onChange={(e) => setHersteller(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="modell-modell">Modell</label>
            <input id="modell-modell" className="input" value={modell} onChange={(e) => setModell(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="modell-verkaufsende">Verkaufsende</label>
            <input
              id="modell-verkaufsende"
              className="input"
              type="date"
              value={verkaufsende}
              onChange={(e) => setVerkaufsende(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="modell-supportende">Support-Ende</label>
            <input
              id="modell-supportende"
              className="input"
              type="date"
              value={supportende}
              onChange={(e) => setSupportende(e.target.value)}
            />
          </div>
          <div className="field grow">
            <label htmlFor="modell-url">Quelle (Link)</label>
            <input
              id="modell-url"
              className="input"
              placeholder="https://"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="btn btn-sm"
            disabled={speichern.isPending || !hersteller.trim() || !modell.trim() || (!verkaufsende && !supportende)}
          >
            Modell eintragen
          </button>
          {speichern.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span>
          ) : null}
        </form>
      ) : null}
    </section>
  )
}
